import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Star, Users as UsersIcon } from 'lucide-react';
import NavBar from '../components/nav';
import { Footer } from '../components/footer';
import { getFilteredRestaurants } from '../services/filtersApi';
import { getRestaurantBannerUrl, getJpegFallbackUrl } from '../services/api';
import { getDisplayRating } from '../utils/ratingStars';
import namkeFallback from '../assets/namke-fallback.png';

const PRIMARY = '#054625';
const TERRA = '#EF4C23';

const MODE_KEYS = ['all', 'open', 'pickup'];
const SORT_VALUES = ['rating', 'name', 'created_at'];

const formatEuro = (n) => `${Number(n || 0).toFixed(2).replace('.', ',')} €`;

const RestaurantGridCard = ({ restaurant }) => {
  const { t } = useTranslation();
  const displayRating = getDisplayRating(restaurant, 0);
  const isOpen = restaurant.is_active !== false;
  const image = getRestaurantBannerUrl(restaurant, 'large');

  return (
    <Link
      to={`/restaurant/${restaurant.id}`}
      style={{
        background: '#FCFBF5',
        border: '1px solid rgba(5,16,4,0.1)',
        borderRadius: '22px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        color: '#051004',
        transition: 'box-shadow 160ms ease',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 10px 22px rgba(5,16,4,0.08)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', background: '#FBF9D9', overflow: 'hidden' }}>
        <img
          src={image || namkeFallback}
          alt={restaurant.name}
          loading="lazy"
          decoding="async"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={(e) => {
            const fb = getJpegFallbackUrl(image);
            if (fb && e.target.dataset.fb !== '1') {
              e.target.dataset.fb = '1';
              e.target.src = fb;
              return;
            }
            e.target.src = namkeFallback;
          }}
        />
        <span
          style={{
            position: 'absolute', top: '14px', left: '14px',
            background: isOpen ? '#72B744' : 'rgba(5,16,4,0.5)', color: isOpen ? '#051004' : '#FBF9D9',
            fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase',
            padding: '6px 13px', borderRadius: '999px',
          }}
        >
          {isOpen ? t('restaurantsList.open') : t('restaurantsList.closed')}
        </span>
        {restaurant.accept_pickup && (
          <span
            style={{
              position: 'absolute', top: '14px', right: '14px',
              background: '#FCFBF5', color: PRIMARY,
              fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase',
              padding: '6px 13px', borderRadius: '999px',
            }}
          >
            {t('restaurantsList.pickup')}
          </span>
        )}
      </div>

      <div style={{ padding: '22px 20px 20px', display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
          <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '20px', lineHeight: 1.15 }}>
            {restaurant.name}
          </strong>
          <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '4px', fontFamily: 'Archivo, sans-serif', fontSize: '13.5px', fontWeight: 600, color: PRIMARY, whiteSpace: 'nowrap' }}>
            <Star size={13} style={{ color: '#FFC717', fill: '#FFC717' }} />
            {displayRating > 0 ? displayRating.toFixed(1) : '—'}
          </span>
        </div>

        <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '13.5px', color: 'rgba(5,16,4,0.6)' }}>
          {[restaurant.cuisine_type, restaurant.city].filter(Boolean).join(' · ') || t('restaurantsList.defaultCuisine')}
        </span>

        <p style={{ margin: 0, fontSize: '14.5px', lineHeight: 1.5, color: 'rgba(5,16,4,0.72)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {restaurant.description || t('restaurantsList.defaultDescription')}
        </p>

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: 'auto', paddingTop: '12px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', background: '#FBF9D9', padding: '7px 13px', borderRadius: '999px' }}>
            {Number(restaurant.delivery_fee) > 0 ? t('restaurantsList.deliveryFee', { price: formatEuro(restaurant.delivery_fee) }) : t('restaurantsList.freeDelivery')}
          </span>
          {Number(restaurant.minimum_order) > 0 && (
            <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', background: '#FBF9D9', padding: '7px 13px', borderRadius: '999px' }}>
              {t('restaurantsList.minimumOrder', { price: formatEuro(restaurant.minimum_order) })}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

export default function Restaurants() {
  const { t } = useTranslation();
  const MODES = MODE_KEYS.map((key) => ({ key, label: t(`restaurantsList.modes.${key}`) }));
  const SORT_OPTIONS = SORT_VALUES.map((value) => ({ value, label: t(`restaurantsList.sortOptions.${value}`) }));
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('all'); // all | open | pickup
  const [selectedCuisine, setSelectedCuisine] = useState(null);
  const [sortBy, setSortBy] = useState('rating');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await getFilteredRestaurants({
          filters: {
            cuisine: selectedCuisine ? [selectedCuisine] : [],
            status: mode === 'open' ? ['open'] : [],
          },
          skip: 0,
          limit: 100,
          sortBy,
          sortOrder: sortBy === 'name' ? 'asc' : 'desc',
        });
        if (cancelled) return;
        setRestaurants(Array.isArray(data) ? data : (data?.restaurants || []));
      } catch (e) {
        console.error('Erreur lors du chargement des restaurants:', e);
        if (!cancelled) setRestaurants([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedCuisine, mode, sortBy]);

  const cuisineOptions = useMemo(() => {
    const set = new Set(restaurants.map((r) => (r.cuisine_type || '').trim()).filter(Boolean));
    return Array.from(set).sort();
  }, [restaurants]);

  const filteredRestaurants = useMemo(() => {
    if (mode !== 'pickup') return restaurants;
    return restaurants.filter((r) => r.accept_pickup === true);
  }, [restaurants, mode]);

  const toggleCuisine = (cuisine) => {
    setSelectedCuisine((prev) => (prev === cuisine ? null : cuisine));
  };

  return (
    <>
      <NavBar />

      <section style={{ background: '#FBF9D9', paddingTop: 'clamp(96px, 12vw, 132px)', paddingBottom: 'clamp(28px, 4vw, 44px)', position: 'relative', overflow: 'hidden' }}>
        <span style={{ position: 'absolute', right: '-120px', top: '-100px', width: '380px', height: '380px', borderRadius: '999px', background: '#72B744', opacity: 0.16 }} />
        <div style={{ position: 'relative', maxWidth: '1320px', margin: '0 auto', padding: '0 clamp(16px, 3vw, 40px)', display: 'flex', flexDirection: 'column', gap: '18px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '13px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: TERRA }}>
            {restaurants.length > 0 ? t('restaurantsList.eyebrowCount', { count: restaurants.length }) : t('restaurantsList.eyebrowDefault')}
          </span>
          <h1 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(34px, 5.2vw, 68px)', lineHeight: 0.98, letterSpacing: '-0.025em', maxWidth: '20ch' }}>
            {t('restaurantsList.title')}
          </h1>
          <p style={{ margin: 0, fontSize: 'clamp(16px, 1.3vw, 19px)', lineHeight: 1.6, maxWidth: '52ch', color: 'rgba(5,16,4,0.76)' }}>
            {t('restaurantsList.subtitle')}
          </p>
        </div>
      </section>

      <main style={{ maxWidth: '1320px', margin: '0 auto', padding: 'clamp(24px, 3vw, 36px) clamp(16px, 3vw, 40px) clamp(48px, 6vw, 88px)', display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <div className="flex flex-wrap" style={{ alignItems: 'center', gap: '12px' }}>
          <div style={{ display: 'flex', gap: '4px', background: '#FBF9D9', borderRadius: '999px', padding: '5px' }}>
            {MODES.map((m) => (
              <button
                key={m.key}
                type="button"
                onClick={() => setMode(m.key)}
                style={{
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 600,
                  fontSize: '14.5px',
                  padding: '11px 26px',
                  borderRadius: '999px',
                  whiteSpace: 'nowrap',
                  background: mode === m.key ? PRIMARY : 'transparent',
                  color: mode === m.key ? '#FBF9D9' : 'rgba(5,16,4,0.66)',
                }}
              >
                {m.label}
              </button>
            ))}
          </div>

          {cuisineOptions.length > 0 && (
            <div className="flex flex-wrap" style={{ gap: '8px' }}>
              {cuisineOptions.map((cuisine) => {
                const active = selectedCuisine === cuisine;
                return (
                  <button
                    key={cuisine}
                    type="button"
                    onClick={() => toggleCuisine(cuisine)}
                    style={{
                      cursor: 'pointer',
                      fontFamily: 'Archivo, sans-serif',
                      fontSize: '14px',
                      fontWeight: 500,
                      padding: '10px 18px',
                      borderRadius: '999px',
                      whiteSpace: 'nowrap',
                      background: active ? PRIMARY : '#FCFBF5',
                      color: active ? '#FBF9D9' : '#051004',
                      border: active ? 'none' : '1px solid rgba(5,16,4,0.14)',
                    }}
                  >
                    {cuisine}
                  </button>
                );
              })}
            </div>
          )}

          <label
            style={{
              marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '8px',
              background: '#FBF9D9', borderRadius: '999px', padding: '10px 18px',
              fontFamily: 'Archivo, sans-serif', fontSize: '14px', color: '#051004', whiteSpace: 'nowrap',
            }}
          >
            {t('restaurantsList.sortLabel')}
            <select
              aria-label={t('restaurantsList.sortAriaLabel')}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{ border: 'none', background: 'transparent', fontFamily: 'Archivo, sans-serif', fontSize: '14px', fontWeight: 600, color: '#051004', outline: 'none', cursor: 'pointer' }}
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </label>
        </div>

        <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '14px', color: 'rgba(5,16,4,0.66)' }}>
          {loading ? t('restaurantsList.loading') : t('restaurantsList.cookCount', { count: filteredRestaurants.length })}
        </span>

        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))', gap: 'clamp(16px, 2vw, 24px)' }}>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="animate-pulse" style={{ background: '#FCFBF5', border: '1px solid rgba(5,16,4,0.1)', borderRadius: '22px', overflow: 'hidden' }}>
                <div style={{ width: '100%', aspectRatio: '16 / 9', background: '#FBF9D9' }} />
                <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ height: '18px', width: '60%', background: '#FBF9D9', borderRadius: '999px' }} />
                  <div style={{ height: '12px', width: '80%', background: '#FBF9D9', borderRadius: '999px' }} />
                </div>
              </div>
            ))}
          </div>
        ) : filteredRestaurants.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '56px 0' }}>
            <UsersIcon size={40} style={{ margin: '0 auto 14px', color: 'rgba(5,16,4,0.2)' }} />
            <strong style={{ display: 'block', fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '19px', marginBottom: '6px' }}>{t('restaurantsList.noneFound')}</strong>
            <span style={{ fontSize: '14px', color: 'rgba(5,16,4,0.55)' }}>{t('restaurantsList.noneFoundHint')}</span>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))', gap: 'clamp(16px, 2vw, 24px)' }}>
            {filteredRestaurants.map((restaurant) => (
              <RestaurantGridCard key={restaurant.id} restaurant={restaurant} />
            ))}
          </div>
        )}

        <div
          style={{
            background: '#FBF9D9',
            borderRadius: 'clamp(28px, 4vw, 44px)',
            padding: 'clamp(28px, 4vw, 52px)',
            display: 'flex',
            flexDirection: 'column',
            gap: '14px',
            alignItems: 'flex-start',
            marginTop: 'clamp(20px, 3vw, 32px)',
          }}
        >
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '13px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: TERRA }}>
            {t('restaurantsList.cta.eyebrow')}
          </span>
          <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(24px, 3vw, 40px)', lineHeight: 1.04, letterSpacing: '-0.02em', maxWidth: '22ch' }}>
            {t('restaurantsList.cta.title')}
          </h2>
          <p style={{ margin: 0, maxWidth: '44ch', fontSize: '15.5px', lineHeight: 1.6, color: 'rgba(5,16,4,0.78)' }}>
            {t('restaurantsList.cta.description')}
          </p>
          <Link
            to="/business/signup"
            style={{ background: TERRA, color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '15px', padding: '14px 28px', borderRadius: '999px' }}
          >
            {t('restaurantsList.cta.button')}
          </Link>
        </div>
      </main>

      <Footer />
    </>
  );
}
